import { z } from "zod";

import { QuizCompletionSchema, QuizGeneralAnswerSchema, QuizIdSchema } from "../models";

// Create Quiz Data schema (without some fields that are set by backend)
export const CreateQuizCompletionSchema = QuizCompletionSchema.omit({
  userId: true,
  completed: true,
  completedAt: true,
  score: true,
  lastUpdated: true
});
export type CreateQuizCompletion = z.infer<typeof CreateQuizCompletionSchema>;

// Update Quiz Completion schema (partial)
export const UpdateQuizCompletionSchema = QuizCompletionSchema.partial().omit({
  userId: true,
  completedAt: true,
  score: true,
  lastUpdated: true
});
export type UpdateQuizCompletion = z.infer<typeof UpdateQuizCompletionSchema>;

// Quiz Answer Update schema
export const UpdateQuizAnswerSchema = z.object({
  questionId: QuizIdSchema,
  answer: QuizGeneralAnswerSchema
});
export type UpdateQuizAnswer = z.infer<typeof UpdateQuizAnswerSchema>;

// Quiz Recommendations schema
export const QuizRecommendationsSchema = z.object({
  datingStyle: z.string(),
  communicationPreferences: z.array(z.string()),
  idealMatch: z.string(),
  compatibilityFactors: z.array(z.string()),
  tips: z.array(z.string())
});
export type QuizRecommendations = z.infer<typeof QuizRecommendationsSchema>;

// ------------------------------------------------------------------------------------------------
// Helper function to calculate quiz score based on answers
export const calculateQuizScore = (answers: Record<string, any>): number => {
  let score = 0;
  const totalQuestions = Object.keys(answers).length;

  if (totalQuestions === 0) return 0;

  // Basic scoring: each answered question gets points
  Object.values(answers).forEach((answer) => {
    if (answer && (typeof answer === "string" || Array.isArray(answer) || typeof answer === "number")) {
      if (Array.isArray(answer) && answer.length > 0) {
        score += 15; // Multiple choice questions get more points
      } else if (typeof answer === "string" && answer.trim()) {
        score += 10; // Single choice questions
      } else if (typeof answer === "number") {
        score += 10; // Scale/degree questions
      }
    }
  });

  // Normalize to 100
  const maxPossibleScore = totalQuestions * 15;
  return Math.round((score / maxPossibleScore) * 100);
};

// Helper function to get quiz recommendations based on answers
export const getQuizRecommendations = (answers: Record<string, any>): QuizRecommendations => {
  const recommendations: QuizRecommendations = {
    datingStyle: "Balanced",
    communicationPreferences: ["Face-to-face", "Text messages"],
    idealMatch: "Someone who shares your values and communication style",
    compatibilityFactors: ["Shared values", "Communication style", "Life goals"],
    tips: [
      "Be open and honest about your expectations",
      "Take time to understand your partner's perspective",
      "Communicate regularly and clearly"
    ]
  };

  // Analyze answers to provide personalized recommendations
  if (answers["1"]) {
    // Ideal first date
    const datePreference = answers["1"];
    if (datePreference === "Coffee or casual drinks") {
      recommendations.datingStyle = "Casual and Low-pressure";
    } else if (datePreference === "Dinner at a nice restaurant") {
      recommendations.datingStyle = "Traditional and Romantic";
    } else if (datePreference === "Outdoor adventure/activity") {
      recommendations.datingStyle = "Adventurous and Active";
    }
  }

  if (answers["2"]) {
    // Communication preference
    const commPreference = answers["2"];
    if (commPreference === "Face-to-face conversations") {
      recommendations.communicationPreferences = ["Face-to-face", "Video calls"];
    } else if (commPreference === "Text messages") {
      recommendations.communicationPreferences = ["Text messages", "Social media"];
    }
  }

  if (answers["5"]) {
    // Love language
    const loveLanguage = answers["5"];
    recommendations.tips.push(`Focus on expressing love through ${loveLanguage.toLowerCase()}`);
  }

  return recommendations;
};
